import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Buttons, LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";
import { formatDate } from "capitalrx-utilities";

import { useGetMemberNotificationQuery, useUpdateMemberNotificationMutation } from "@/App/api/notification";
import { ContentPanel, PageWrapper, ThemedButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClient } from "@/App/hooks";
import { getCustomerSupportNumber } from "@/App/utils/utils";
import { LCAMessage } from "./LCAMessage";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { AdherenceMessage } from "./AdherenceMessage";
import { GeneralMessage } from "./GeneralMessage";
import { DMRMessage } from "./DMRMessage";
import { PAMessage } from "./PAMessage";

export const MessageDetailPage: FC = () => {
  const { t: translate } = useTranslation();
  const { messageId } = useParams();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { data: memberNotification, isLoading: isLoadingMemberNotification } = useGetMemberNotificationQuery(+messageId);
  const updateMemberNotificationMutation = useUpdateMemberNotificationMutation();
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility);
  const isEmulating = !!sessionStorage.getItem("isEmulation");
  const alert = useAlert();
  const navigate = useNavigateClient();

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const ContentFlowContainer = isCompressed ? Stack : Shelf;

  useEffect(() => {
    if (!isViewingDataholder && !isEmulating && memberNotification && memberNotification.read_status === "unread") {
      updateMemberNotificationMutation.mutate({
        id: memberNotification.id,
        read_status: "read",
      });
    }
  }, [memberNotification]);

  const programClass: "lca" | "adherence" | "dmr" | "pa" | undefined = useMemo(() => {
    const program = memberNotification?.program;
    if (program && program.includes("therapeutic_substitutions")) {
      return "lca";
    } else if (program && program.includes("adherence")) {
      return "adherence";
    } else if (program && program.includes("direct_member_reimbursements")) {
      return "dmr";
    } else if (program && (program.includes("pa_renewals") || program.includes("pa_cases"))) {
      return "pa";
    }
    return undefined;
  }, [memberNotification]);
  const supplementalMessage = memberNotification?.json_message?.supplemental_message;

  const onDeleteMessage = useCallback(async () => {
    try {
      await updateMemberNotificationMutation.mutateAsync({
        read_status: "hide",
        id: +messageId,
      });
      alert({
        type: "success",
        style: "toast",
        message: translate("MESSAGE_DETAIL.messageDetailPage.messageRemoved"),
      });

      if (programClass === "lca") {
        logEvent(ANALYTIC_EVENTS.INBOX.LOW_COST_ALTERNATIVE.lcaMessageDelete);
      } else if (programClass === "adherence") {
        logEvent(ANALYTIC_EVENTS.INBOX.ADHERENCE.adhMessageDelete);
      }

      navigate("/inbox");
    } catch {
      alert({
        type: "error",
        style: "toast",
        message: translate("MESSAGE_DETAIL.messageDetailPage.unableToDeleteMessage"),
      });
    }
  }, []);

  const onCallUs = useCallback(() => {
    document.getElementById("dial-support").click();
  }, []);

  const onBack = useCallback(() => {
    navigate("/inbox");
  }, []);

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <LoadingGroup loading={isLoadingMemberNotification}>
            <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 48 }} />}>
              <ContentFlowContainer>
                <ThemedButton testId="go-back" appearance="secondary" icon="arrow-left" onClick={onBack}>
                  {translate("MESSAGE_DETAIL.messageDetailPage.back")}
                </ThemedButton>
                <Subheading testId="member-notification-subject">
                  {memberNotification?.subject?.startsWith("MESSAGE_DETAIL.") ? translate(memberNotification.subject) : memberNotification?.subject}
                </Subheading>
                <Shelf.Spacer />
                {formatDate(memberNotification?.created_at, "MMMM D, YYYY")}
              </ContentFlowContainer>
            </LoadingGroup.Placeholder>
          </LoadingGroup>
        }
      >
        <Grid>
          {supplementalMessage && programClass === "lca" && (
            <Grid.Span columns={12}>
              <LCAMessage memberNotification={memberNotification} />
            </Grid.Span>
          )}
          {supplementalMessage && programClass === "adherence" && (
            <Grid.Span columns={12}>
              <AdherenceMessage memberNotification={memberNotification} />
            </Grid.Span>
          )}
          {programClass === "dmr" && memberNotification && (
            <Grid.Span columns={12}>
              <DMRMessage memberNotification={memberNotification} />
            </Grid.Span>
          )}
          {programClass === "pa" && memberNotification && (
            <Grid.Span columns={12}>
              <PAMessage memberNotification={memberNotification} />
            </Grid.Span>
          )}
          {programClass === undefined && memberNotification && (
            <Grid.Span columns={12}>
              <GeneralMessage memberNotification={memberNotification} />
            </Grid.Span>
          )}
          <Grid.Span columns={12}>
            <ContentFlowContainer>
              <ThemedButton testId="call-us" appearance="primary" icon="phone" onClick={onCallUs}>
                {translate("MESSAGE_DETAIL.messageDetailPage.callUs")}: {customerSupportNumber}
                <a
                  style={{ visibility: "hidden", display: "none" }}
                  id="dial-support"
                  aria-hidden="true"
                  href={`tel:${customerSupportNumber}`}
                >
                  {translate("MESSAGE_DETAIL.messageDetailPage.callUs")}
                </a>
              </ThemedButton>
              <Shelf.Spacer />
              <ThemedButton
                testId="delete-message"
                onClick={onDeleteMessage}
                disabled={isViewingDataholder || isEmulating}
                appearance={Buttons.Destructive.Secondary.Large}
              >
                {translate("MESSAGE_DETAIL.messageDetailPage.deleteMessage")}
              </ThemedButton>
            </ContentFlowContainer>
          </Grid.Span>
        </Grid>
      </ContentPanel>
    </PageWrapper>
  );
};
